/**
 * Timezone-safe helpers for calendar/availability dates.
 *
 * Problem: The backend stores availability and search dates as pure
 * `YYYY-MM-DD` strings (no time component). Two common JS mistakes
 * corrupt these:
 *
 *   • `new Date("2026-03-15")` → parses as UTC midnight; when formatted
 *     with the local locale in a negative UTC-offset timezone the day
 *     shifts back by 1.
 *   • `today.toISOString().split('T')[0]` → converts today's *local*
 *     midnight to UTC (BST=UTC+1 → 23:00 previous day) and slices; the
 *     resulting "today" is yesterday for anyone west of UTC.
 *
 * These helpers pin every conversion to the *local* calendar day so a
 * plumber on 2026-03-15 always reads back as 2026-03-15 regardless of
 * the browser's timezone.
 */

/**
 * Parse a `YYYY-MM-DD` string into a local-midnight Date. Returns null
 * for empty / invalid input.
 */
export function parseLocalDate(ymd) {
  if (!ymd || typeof ymd !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d));
}

/**
 * Format a Date (or `YYYY-MM-DD` string) as the same `YYYY-MM-DD` in
 * the local timezone. Never uses `toISOString`.
 */
export function toLocalYMD(dateOrYmd) {
  if (!dateOrYmd) return '';
  const d = typeof dateOrYmd === 'string' ? parseLocalDate(dateOrYmd) : dateOrYmd;
  if (!d || Number.isNaN(d.getTime())) return '';
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Today, as a local `YYYY-MM-DD` string. */
export function todayYMD() {
  return toLocalYMD(new Date());
}

/** Add `n` days to a `YYYY-MM-DD` string and return the new `YYYY-MM-DD`. */
export function addDaysYMD(ymd, n) {
  const d = parseLocalDate(ymd);
  if (!d) return ymd;
  d.setDate(d.getDate() + n);
  return toLocalYMD(d);
}

/**
 * Locale-format a `YYYY-MM-DD` string as a human-friendly date using
 * the *local* calendar day (avoiding the UTC-midnight shift).
 */
export function formatYMD(ymd, options = { weekday: 'short', day: 'numeric', month: 'short' }, locale = 'en-GB') {
  const d = parseLocalDate(ymd);
  if (!d) return ymd || '';
  return d.toLocaleDateString(locale, options);
}
